import type { PrimarySelection, TreeSelection } from '../types/filters';

/**
 * Converts primary selections to regular tree selections, filtering only active ones
 */
export const getActiveSelections = (
  primarySelections: PrimarySelection[]
): TreeSelection[] => {
  return primarySelections
    .filter((sel) => sel.isActive)
    .map(({ isActive: _isActive, ...rest }) => rest);
};

/**
 * Ensures at least one selection is active
 */
export const enforceMinSelections = (
  primarySelections: PrimarySelection[],
  minimumCount = 1
): PrimarySelection[] => {
  const activeCount = primarySelections.filter((sel) => sel.isActive).length;

  if (activeCount >= minimumCount) {
    return primarySelections;
  }

  const inactiveSelections = primarySelections.filter((sel) => !sel.isActive);
  if (inactiveSelections.length === 0) {
    return primarySelections;
  }

  // Activate the first few inactive selections to meet minimum
  const neededActive = minimumCount - activeCount;
  let activatedCount = 0;

  return primarySelections.map((sel) => {
    if (!sel.isActive && activatedCount < neededActive) {
      activatedCount++;
      return { ...sel, isActive: true };
    }
    return sel;
  });
};

/**
 * Enforces the selection limit on primary selections
 */
export const enforceMaxSelections = (
  primarySelections: PrimarySelection[],
  limit: number
): PrimarySelection[] => {
  const activeSelections = primarySelections.filter((sel) => sel.isActive);

  if (activeSelections.length <= limit) {
    return primarySelections;
  }

  // Only keep the first N active
  let count = 0;
  return primarySelections.map((sel) => {
    if (sel.isActive) {
      count++;
      return { ...sel, isActive: count <= limit };
    }
    return sel;
  });
};

/**
 * Removes a selection while ensuring minimum active count is maintained
 */
export const removeSelection = (
  primarySelections: PrimarySelection[],
  selectionIdToRemove: string
): {
  canRemove: boolean;
  updatedSelections: PrimarySelection[];
} => {
  const activeSelections = primarySelections.filter((sel) => sel.isActive);
  const selectionToRemove = primarySelections.find(
    (sel) => sel.id === selectionIdToRemove
  );
  const isRemovingActive = selectionToRemove?.isActive || false;

  // If trying to remove the last active filter and there are no inactive ones
  if (isRemovingActive && activeSelections.length === 1) {
    const inactiveSelections = primarySelections.filter((sel) => !sel.isActive);

    if (inactiveSelections.length === 0) {
      return { canRemove: false, updatedSelections: primarySelections };
    }

    // Activate the first inactive filter and remove the requested one
    const firstInactiveId = inactiveSelections[0]?.id;
    const updatedSelections = primarySelections
      .filter((sel) => sel.id !== selectionIdToRemove)
      .map((sel) =>
        sel.id === firstInactiveId ? { ...sel, isActive: true } : sel
      );

    return { canRemove: true, updatedSelections };
  }

  const updatedSelections = primarySelections.filter(
    (sel) => sel.id !== selectionIdToRemove
  );

  return { canRemove: true, updatedSelections };
};

/**
 * Activates a selection with limit enforcement
 */
export const activateSelection = (
  primarySelections: PrimarySelection[],
  selectionIdToActivate: string,
  limit: number
): PrimarySelection[] => {
  const activeCount = primarySelections.filter((sel) => sel.isActive).length;
  const overLimit = activeCount >= limit;

  if (!overLimit) {
    return primarySelections.map((sel) =>
      sel.id === selectionIdToActivate ? { ...sel, isActive: true } : sel
    );
  }

  // Over limit: deactivate the last active item (by index), then activate the requested one
  const lastActiveIdx = [...primarySelections].reduce(
    (acc, sel, idx) => (sel.isActive ? idx : acc),
    -1
  );

  return primarySelections.map((sel, idx) => {
    if (sel.id === selectionIdToActivate) return { ...sel, isActive: true };
    if (idx === lastActiveIdx) return { ...sel, isActive: false };
    return sel;
  });
};

export const initializeSelections = (
  groupLevelPrimarySelections: PrimarySelection[],
  defaultActiveCount: number
): PrimarySelection[] => {
  if (groupLevelPrimarySelections.length === 0) {
    return [];
  }

  const inactiveSelections = groupLevelPrimarySelections.map((sel) => ({
    ...sel,
    isActive: false,
  }));

  // Activate the first N selections up to the default count
  const activatedSelections = inactiveSelections.map((sel, index) => ({
    ...sel,
    isActive: index < defaultActiveCount,
  }));

  return activatedSelections;
};

/**
 * Extracts the leaf (entity-level) ID from a nested tree ID.
 * e.g. "8_263_100525442" → "100525442", "100525442" → "100525442"
 */
const getLeafId = (id: string): string => {
  const parts = id.split('_');
  return parts[parts.length - 1] ?? id;
};

/**
 * Builds a unique key from a selection's leaf ID and level.
 * Leaf IDs alone can collide across tree levels (e.g. country id "1" vs
 * MSA with path "5_4_1" both produce leaf "1"). Including levelId
 * prevents those false matches while still allowing reclassified entities
 * (same level, different parent path) to be recognised.
 *
 * Exported for `reconcilePageSelections` so cross-page restore matches
 * `updatePrimarySelections` when tree paths change (e.g. Reference API).
 */
export const getPrimarySelectionMatchKey = (s: {
  id: string;
  levelId: string;
}): string => `${s.levelId}:${getLeafId(s.id)}`;

export const updatePrimarySelections = (
  newSelections: TreeSelection[],
  currentPageSelections: PrimarySelection[],
  limit: number
): PrimarySelection[] => {
  // Build lookup by level-qualified leaf ID so reclassified entities
  // (same company, different industry path) are recognised as existing
  // selections, without colliding across different tree levels.
  const currentKeySet = new Set(
    currentPageSelections.map(getPrimarySelectionMatchKey)
  );
  const newKeySet = new Set(newSelections.map(getPrimarySelectionMatchKey));

  const added = newSelections
    .filter((s) => !currentKeySet.has(getPrimarySelectionMatchKey(s)))
    .map(
      (s): PrimarySelection => ({
        ...s,
        isActive: true,
      })
    );

  // Keep existing selections that are still present in newSelections,
  // but update their id/name/levelId to match the latest tree data.
  const kept = currentPageSelections
    .filter((s) => newKeySet.has(getPrimarySelectionMatchKey(s)))
    .map((s) => {
      const key = getPrimarySelectionMatchKey(s);
      const updated = newSelections.find(
        (ns) => getPrimarySelectionMatchKey(ns) === key
      );
      if (updated && updated.id !== s.id) {
        return {
          ...s,
          id: updated.id,
          name: updated.name,
          levelId: updated.levelId,
        };
      }
      return s;
    });

  let combined = [...added, ...kept];
  combined = enforceMaxSelections(combined, limit);
  return enforceMinSelections(combined, 1);
};
